import { useState, useEffect } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { LogOut, Plus, Pencil, Trash2, Eye } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout }) {
  // Page title
  usePageTitle('Admin Dashboard');
  
  const [competitions, setCompetitions] = useState([]);
  const [selectedComp, setSelectedComp] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [showEntriesDialog, setShowEntriesDialog] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    startsOn: '',
    endsOn: '',
    isOpen: true,
    entryFee: 40
  });

  useEffect(() => {
    loadCompetitions();
  }, []);

  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      setCompetitions(response.data);
    } catch (error) {
      console.error('Failed to load competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  const loadEntries = async (compId) => {
    try {
      const response = await axios.get(`${API}/admin/entries/${compId}`);
      setEntries(response.data);
      setSelectedComp(competitions.find(c => c.id === compId));
      setShowEntriesDialog(true);
    } catch (error) {
      console.error('Failed to load entries:', error);
      toast.error('Failed to load entries');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOpenDialog = (comp = null) => {
    if (comp) {
      setEditingComp(comp);
      setFormData({
        title: comp.title,
        startsOn: comp.startsOn,
        endsOn: comp.endsOn,
        isOpen: comp.isOpen,
        entryFee: comp.entryFee
      });
    } else {
      setEditingComp(null);
      setFormData({
        title: '',
        startsOn: '',
        endsOn: '',
        isOpen: true,
        entryFee: 40
      });
    }
    setShowCompDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingComp) {
        await axios.put(`${API}/competitions/${editingComp.id}`, formData);
        toast.success('Competition updated successfully');
      } else {
        await axios.post(`${API}/competitions`, formData);
        toast.success('Competition created successfully');
      }
      setShowCompDialog(false);
      loadCompetitions();
    } catch (error) {
      console.error('Failed to save competition:', error);
      toast.error('Failed to save competition');
    }
  };

  const handleDelete = async (compId) => {
    if (!window.confirm('Are you sure you want to delete this competition?')) return;

    try {
      await axios.delete(`${API}/competitions/${compId}`);
      toast.success('Competition deleted successfully');
      loadCompetitions();
    } catch (error) {
      console.error('Failed to delete competition:', error);
      toast.error('Failed to delete competition');
    }
  };

  const exportToCSV = () => {
    if (!entries || entries.length === 0) {
      toast.error('No entries to export');
      return;
    }

    const headers = ['Club Name', 'Email', 'Phone', 'Gymnasts', 'Coaches', 'Judges', 'Total', 'Submitted At'];
    const rows = entries.map(entry => [
      entry.club?.name || '',
      entry.club?.email || '',
      entry.club?.phone || '',
      entry.gymnasts?.map(g => `${g.firstName} ${g.lastName}`).join('; ') || '',
      entry.coaches?.map(c => `${c.firstName} ${c.lastName}`).join('; ') || '',
      entry.judges?.map(j => `${j.firstName} ${j.lastName}`).join('; ') || '',
      `£${entry.total?.toFixed(2) || '0.00'}`,
      entry.submittedAt ? format(new Date(entry.submittedAt), 'dd/MM/yyyy HH:mm') : 'Not submitted'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedComp?.title || 'competition'}_entries.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Entries exported successfully');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-16 w-auto" />
            </div>
            <Button
              data-testid="admin-logout-button"
              onClick={onLogout}
              variant="outline"
              className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Competitions</h2>
          <Button
            data-testid="create-competition-button"
            onClick={() => handleOpenDialog()}
            className="bg-[#000000] hover:bg-[#333333]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Competition
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {competitions.map(comp => (
            <Card key={comp.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{comp.title}</CardTitle>
                    <CardDescription className="mt-1">
                      {format(new Date(comp.startsOn), 'dd/MM/yyyy')} - {format(new Date(comp.endsOn), 'dd/MM/yyyy')}
                    </CardDescription>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      comp.isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {comp.isOpen ? 'Open' : 'Closed'}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <p className="text-sm text-gray-600">Entry Fee: <span className="font-semibold text-[#000000]">£{comp.entryFee}</span></p>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`view-entries-button-${comp.id}`}
                    onClick={() => loadEntries(comp.id)}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Entries
                  </Button>
                  <Button
                    data-testid={`edit-competition-button-${comp.id}`}
                    onClick={() => handleOpenDialog(comp)}
                    size="sm"
                    variant="outline"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-competition-button-${comp.id}`}
                    onClick={() => handleDelete(comp.id)}
                    size="sm"
                    variant="outline"
                    className="text-gray-900 hover:bg-gray-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {competitions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No competitions yet. Create your first competition!</p>
          </div>
        )}
      </main>

      {/* Competition Form Dialog */}
      <Dialog open={showCompDialog} onOpenChange={setShowCompDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingComp ? 'Edit Competition' : 'Create Competition'}</DialogTitle>
            <DialogDescription>
              {editingComp ? 'Update competition details' : 'Add a new competition'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Competition Title</Label>
              <Input
                id="title"
                name="title"
                data-testid="competition-title-input"
                required
                value={formData.title}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="startsOn">Start Date</Label>
              <Input
                id="startsOn"
                name="startsOn"
                data-testid="competition-start-date-input"
                type="date"
                required
                value={formData.startsOn}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="endsOn">End Date</Label>
              <Input
                id="endsOn"
                name="endsOn"
                data-testid="competition-end-date-input"
                type="date"
                required
                value={formData.endsOn}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="entryFee">Entry Fee (£)</Label>
              <Input
                id="entryFee"
                name="entryFee"
                data-testid="competition-entry-fee-input"
                type="number"
                step="0.01"
                min="0"
                required
                value={formData.entryFee}
                onChange={handleChange}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="isOpen"
                data-testid="competition-is-open-switch"
                checked={formData.isOpen}
                onCheckedChange={(checked) => setFormData({ ...formData, isOpen: checked })}
              />
              <Label htmlFor="isOpen">Competition is Open</Label>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCompDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-competition-button"
                className="flex-1 bg-[#000000] hover:bg-[#333333]"
              >
                {editingComp ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Entries Dialog */}
      <Dialog open={showEntriesDialog} onOpenChange={setShowEntriesDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Entries for {selectedComp?.title}</DialogTitle>
            <DialogDescription>
              View all club entries for this competition
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button
              data-testid="export-entries-button"
              onClick={exportToCSV}
              className="bg-[#000000] hover:bg-[#333333]"
            >
              Export to CSV
            </Button>
            {entries.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No entries yet</p>
            ) : (
              entries.map(entry => (
                <Card key={entry.id} className="border-l-4 border-l-[#000000]">
                  <CardHeader>
                    <CardTitle className="text-lg">{entry.club?.name}</CardTitle>
                    <CardDescription>
                      {entry.club?.email} | {entry.club?.phone}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="font-semibold text-sm mb-1">Gymnasts ({entry.gymnasts?.length || 0}):</p>
                      <p className="text-sm text-gray-700">
                        {entry.gymnasts?.map(g => `${g.firstName} ${g.lastName} (${g.category})`).join(', ') || 'None'}
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-sm mb-1">Coaches ({entry.coaches?.length || 0}):</p>
                      <p className="text-sm text-gray-700">
                        {entry.coaches?.map(c => `${c.firstName} ${c.lastName}`).join(', ') || 'None'}
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-sm mb-1">Judges ({entry.judges?.length || 0}):</p>
                      <p className="text-sm text-gray-700">
                        {entry.judges?.map(j => `${j.firstName} ${j.lastName}`).join(', ') || 'None'}
                      </p>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="font-semibold text-[#000000]">Total: £{entry.total?.toFixed(2) || '0.00'}</span>
                      <span className="text-sm text-gray-600">
                        {entry.submittedAt ? `Submitted: ${format(new Date(entry.submittedAt), 'dd/MM/yyyy HH:mm')}` : 'Not submitted'}
                      </span>
                    </div>
                    {entry.paymentProofUrl && (
                      <div>
                        <a
                          href={`${API}/payment-proof/${entry.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#000000] hover:underline text-sm"
                        >
                          View Payment Proof
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}